import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faCancel } from '@fortawesome/free-solid-svg-icons';
import MapboxMap from './MapboxMap';
import MapToolbar from './MapToolbar'
import CropLegend from './CropLegend';
import { API_ENDPOINTS } from '../constants/api';
import './FieldMapModal.css';
import { toProperCase } from '../utils/textUtils';
import { calculatePolygonAreaFromFeature } from '../utils/geometryUtils';

interface Field {
  field_id: string;
  fieldname: string;
  farm_id: string;
  acres: number;
  geojson: string;
  state: string;
  county: string;
  cdl2024?: string; // Optional - only used when creating fields from map
  climate_zone: string;
  created_at: string;
  updated_at: string;
  crop?: string; // Optional - crop type for existing fields
}

interface FieldMapModalProps {
  farmId: string;
  farmName: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: Field) => void;
  userToken: string;
  selectedOrganizationId?: string; // For Super User mode
  existingFields: Field[];
  isManualDrawMode?: boolean; // New prop for manual draw mode
  targetField?: Field | null; // Field to add boundary to (for addBoundary mode)
}

const FieldMapModal: React.FC<FieldMapModalProps> = ({
  farmId,
  farmName,
  isOpen,
  onClose,
  onSave,
  userToken,
  selectedOrganizationId,
  existingFields,
  isManualDrawMode = false,
  targetField = null
}) => {
  const [selectedPolygon, setSelectedPolygon] = useState<any>(null);
  const [fieldName, setFieldName] = useState('');
  const [fieldState, setFieldState] = useState('');
  const [fieldCounty, setFieldCounty] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savingForStayOpen, setSavingForStayOpen] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [currentCounty, setCurrentCounty] = useState<string | null>(null);
  const [currentCountyData, setCurrentCountyData] = useState<any>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isFieldDrawingMode, setIsFieldDrawingMode] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [polygonsLoaded, setPolygonsLoaded] = useState(false);
  const [boundaryMode, setBoundaryMode] = useState<'draw' | 'select'>('select');
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isInfoPanelModalOpen, setIsInfoPanelModalOpen] = useState(false);
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);
  const [visibleCrops, setVisibleCrops] = useState<string[]>([]);
  const [farmState, setFarmState] = useState<string>('');
  const [farmCounty, setFarmCounty] = useState<string>('');
  const [fieldDrawingPointsCount, setFieldDrawingPointsCount] = useState(0);
  const [isFieldEditingMode, setIsFieldEditingMode] = useState(false);
  const mapRef = useRef<any>(null);
  const previousCountyRef = useRef<string | null>(null);

  // Get unique crops from tileserver data (CDL2024) to show in legend
  const getVisibleCrops = (): string[] => {
    return visibleCrops;
  };

  // Handle visible crops change from MapboxMap
  const handleVisibleCropsChange = (crops: string[]) => {
    setVisibleCrops(crops);
  };

  // Initialize boundary mode based on current onboarding choice
  useEffect(() => {
    // Default to draw (fetch-from-tile-server disabled - boundaries not accurate enough)
    setBoundaryMode('draw');
    
    // Debug: Check if we have a saved loaded county on component mount
    const savedLoadedCounty = getSavedLoadedCounty();

  }, []);

  // Reset drawing/edit states when modal opens to ensure clean state
  useEffect(() => {
    if (isOpen) {
      setIsDrawingMode(false);
      setIsSelectMode(false);
      setIsFieldDrawingMode(false);
      setIsFieldEditingMode(false);
      setFieldDrawingPointsCount(0);
      setSelectedPolygon(null);
      setError('');
      setShowForm(false);
      setShowSuccessMessage(false);
      
      // Pre-fill field data if we're adding boundary to existing field
      if (targetField) {
        setFieldName(targetField.fieldname);
        setFieldState(targetField.state || '');
        setFieldCounty(targetField.county || '');
      } else {
        setFieldName('');
        setFieldState('');
        setFieldCounty('');
      }
      
      // Always use draw mode (fetch polygons from tile server disabled)
      setBoundaryMode('draw');
      
      // Clear any existing county data
      setCurrentCounty(null);
      setCurrentCountyData(null);
    }
  }, [isOpen, isManualDrawMode, targetField]);

  // Smart zoom to farm location when modal opens
  useEffect(() => {
    if (!isOpen || !existingFields || existingFields.length === 0) {
      return;
    }

    console.log('🌾 Smart zoom effect triggered, checking map readiness...');

    // Wait for map ref to be available
    const waitForMap = () => {
      if (!mapRef.current) {
        console.log('🌾 Map ref not ready, waiting...');
        setTimeout(waitForMap, 100);
        return;
      }

      console.log('🌾 Map ref ready, checking if style is loaded...');

      // Wait for map to be fully loaded before calculating bounds
      if (!mapRef.current.isStyleLoaded()) {
        console.log('🌾 Map style not loaded yet, waiting...');
        const checkMapReady = () => {
          if (mapRef.current && mapRef.current.isStyleLoaded()) {
            console.log('🌾 Map style loaded, proceeding with farm bounds calculation');
            // Re-run the effect after a short delay to ensure map is ready
            setTimeout(() => {
              // Trigger the bounds calculation
              calculateAndZoomToFarmBounds();
            }, 100);
          } else {
            setTimeout(checkMapReady, 100);
          }
        };
        checkMapReady();
        return;
      }

      // Map is ready, proceed with bounds calculation
      console.log('🌾 Map is ready, calculating farm bounds...');
      calculateAndZoomToFarmBounds();
    };

    waitForMap();
  }, [isOpen, existingFields]);

  // Separate function for the actual bounds calculation
  const calculateAndZoomToFarmBounds = () => {
    if (!mapRef.current || !existingFields || existingFields.length === 0) {
      return;
    }

    // Function to calculate bounds from existing fields
    const calculateFarmBounds = (fields: Field[]) => {
      let minLng = Infinity, maxLng = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;
      let hasValidBounds = false;

      fields.forEach(field => {
        try {
          const geojson = typeof field.geojson === 'string' ? JSON.parse(field.geojson) : field.geojson;
          if (geojson && geojson.coordinates) {
            const coordinates = geojson.coordinates[0]; // First ring of polygon
            coordinates.forEach((coord: number[]) => {
              // Validate coordinates are reasonable (US bounds roughly)
              if (coord[0] >= -180 && coord[0] <= -50 && coord[1] >= 20 && coord[1] <= 70) {
                minLng = Math.min(minLng, coord[0]);
                maxLng = Math.max(maxLng, coord[0]);
                minLat = Math.min(minLat, coord[1]);
                maxLat = Math.max(maxLat, coord[1]);
                hasValidBounds = true;
              }
            });
          }
        } catch (error) {
          console.warn('Failed to parse field geojson:', field.fieldname, error);
        }
      });

      // Validate bounds are reasonable
      if (hasValidBounds && 
          minLng !== Infinity && maxLng !== -Infinity && 
          minLat !== Infinity && maxLat !== -Infinity &&
          minLng < maxLng && minLat < maxLat &&
          minLng >= -180 && maxLng <= -50 && 
          minLat >= 20 && maxLat <= 70) {
        return [
          [minLng, minLat], // Southwest corner
          [maxLng, maxLat]  // Northeast corner
        ];
      }
      return null;
    };

    // Function to get the most common state/county from existing fields
    const getFarmLocation = (fields: Field[]) => {
      const stateCounts: { [key: string]: number } = {};
      const countyCounts: { [key: string]: number } = {};
      
      fields.forEach(field => {
        if (field.state) {
          stateCounts[field.state] = (stateCounts[field.state] || 0) + 1;
        }
        if (field.county) {
          countyCounts[field.county] = (countyCounts[field.county] || 0) + 1;
        }
      });

      const mostCommonState = Object.keys(stateCounts).reduce((a, b) => 
        stateCounts[a] > stateCounts[b] ? a : b, '');
      const mostCommonCounty = Object.keys(countyCounts).reduce((a, b) => 
        countyCounts[a] > countyCounts[b] ? a : b, '');

      // Convert state name to abbreviation and county name to county key format
      const stateAbbrev = getStateAbbreviation(mostCommonState);
      const countyKey = stateAbbrev && mostCommonCounty ? 
        `${stateAbbrev}_${mostCommonCounty.replace(' County', '').replace(' ', '_')}` : 
        mostCommonCounty;

      return { 
        state: mostCommonState, 
        county: mostCommonCounty,
        countyKey: countyKey,
        stateAbbrev: stateAbbrev
      };
    };

    // Helper function to convert state name to abbreviation
    const getStateAbbreviation = (stateName: string): string => {
      const stateMap: { [key: string]: string } = {
        'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
        'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
        'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
        'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
        'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
        'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
        'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
        'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
        'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
        'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
      };
      return stateMap[stateName] || '';
    };

    // Get saved location from localStorage
    const savedState = localStorage.getItem('lastSelectedState');
    const savedCounty = localStorage.getItem('lastSelectedCounty');

    // Get farm location from existing fields
    const farmLocation = getFarmLocation(existingFields);
    //console.log('🌾 Farm location determined:', farmLocation);
    
    // Priority 1: If there are existing fields with polygons, ALWAYS zoom to them first
    if (existingFields && existingFields.length > 0) {
      // Try to zoom to farm bounds first (highest priority - existing field polygons)
      const farmBounds = calculateFarmBounds(existingFields);
      
      if (farmBounds) {
        //console.log('🌾 Zooming to farm bounds from existing fields');
        // Expand bounds slightly to zoom out by ~1 increment
        const [[swLng, swLat], [neLng, neLat]] = farmBounds;
        const lngDiff = neLng - swLng;
        const latDiff = neLat - swLat;
        const expandedBounds = [
          [swLng - lngDiff * 0.2, swLat - latDiff * 0.2], // Southwest corner expanded outward
          [neLng + lngDiff * 0.2, neLat + latDiff * 0.2]  // Northeast corner expanded outward
        ];
        mapRef.current.fitBounds(expandedBounds, {
          padding: 50,
          duration: 2000,
          essential: true
        });
        
        // Set the county from farm location for consistency
        if (farmLocation.countyKey) {
          setCurrentCounty(farmLocation.countyKey);
          setFarmCounty(farmLocation.countyKey);
          // Update localStorage to match farm location
          if (farmLocation.state) {
            localStorage.setItem('lastSelectedState', farmLocation.state);
            setFarmState(farmLocation.state);
          }
          localStorage.setItem('lastSelectedCounty', farmLocation.countyKey);
          
          // Load county data for the toolbar
          fetch('/counties.json')
            .then(response => response.json())
            .then(counties => {
              //console.log('🌾 Looking for county with key:', farmLocation.countyKey);
              //console.log('🌾 Looking for county with name:', farmLocation.county, 'state:', farmLocation.state);
              
              // Try different county key formats
              let county = counties.find((c: any) => c.county_key === farmLocation.countyKey);
              if (!county) {
                // Try matching by county name and state
                county = counties.find((c: any) => 
                  c.county === farmLocation.county && c.state_name === farmLocation.state);
              }
              if (!county) {
                // Try just county name
                county = counties.find((c: any) => c.county === farmLocation.county);
              }
              
              if (county) {
                console.log('🌾 Found county object:', county);
                setCurrentCountyData(county);
                //console.log('🌾 County data loaded for farm location:', county.county, county.state_name);
              } else {
                console.warn('❌ County data not found for farm location:', farmLocation);
                //console.log('🌾 Available counties sample:', counties.slice(0, 5).map((c: any) => ({ key: c.county_key, name: c.county, state: c.state_name })));
              }
            })
            .catch(error => {
              console.error('❌ Error loading county data for farm location:', error);
            });
        }
        return;
      }
      
      // Fallback: zoom to farm's county if bounds calculation failed
      if (farmLocation.countyKey) {
        console.log('🌾 Zooming to farm county:', farmLocation.countyKey);
        fetch('/counties.json')
          .then(response => response.json())
          .then(counties => {
            // Try different county key formats
            let county = counties.find((c: any) => c.county_key === farmLocation.countyKey);
            if (!county) {
              // Try matching by county name and state
              county = counties.find((c: any) => 
                c.county === farmLocation.county && c.state_name === farmLocation.state);
            }
            if (!county) {
              // Try just county name
              county = counties.find((c: any) => c.county === farmLocation.county);
            }
            
            if (county && county.centroid) {
              const [lng, lat] = county.centroid;
              handleZoomToLocation(lat, lng, 10);
              setCurrentCounty(county.county_key || farmLocation.countyKey);
              setFarmCounty(county.county_key || farmLocation.countyKey);
              setCurrentCountyData(county);
              
              // Update localStorage to match farm location
              if (farmLocation.state) {
                localStorage.setItem('lastSelectedState', farmLocation.state);
                setFarmState(farmLocation.state);
              }
              localStorage.setItem('lastSelectedCounty', county.county_key || farmLocation.countyKey);
            } else {
              console.warn('❌ County not found for farm location:', farmLocation);
            }
          })
          .catch(error => {
            console.error('❌ Error loading counties data for farm location:', error);
          });
        return;
      }
    }

    // Fallback to saved location if farm location is not available
    if (savedCounty) {
      console.log('🌾 Using saved location:', savedCounty);
      setCurrentCounty(savedCounty);
      setFarmCounty(savedCounty);
      if (savedState) {
        setFarmState(savedState);
      }
      // The existing county selection logic will handle the zoom
    }
  };

  // Function to determine if we should show manual draw mode
  const shouldShowManualDrawMode = () => {
    // Simply check if the radio button is set to 'draw'
    return boundaryMode === 'draw';
  };

  // Auto-switch to satellite mode when manual draw mode is enabled
  useEffect(() => {
    // Check if manual draw mode is enabled based on radio selection
    if (boundaryMode === 'draw' && mapRef.current && mapRef.current.switchToSatelliteMode) {

      mapRef.current.switchToSatelliteMode();
    }
  }, [boundaryMode]);

  // Function to calculate area from GeoJSON polygon
  const calculateArea = (polygon: any): number => {
    if (!polygon || !polygon.geometry) return 0;

    try {
      // Use turf.js area calculation or a simple approximation
      // For now, let's use a simple approximation based on the bounding box
      const coordinates = polygon.geometry.coordinates[0]; // First ring of polygon
      if (!coordinates || coordinates.length < 3) return 0;

      // Calculate bounding box
      let minLng = Infinity, maxLng = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;

      coordinates.forEach((coord: number[]) => {
        minLng = Math.min(minLng, coord[0]);
        maxLng = Math.max(maxLng, coord[0]);
        minLat = Math.min(minLat, coord[1]);
        maxLat = Math.max(maxLat, coord[1]);
      });

      // Simple area approximation (not perfect but gives a reasonable estimate)
      const width = maxLng - minLng;
      const height = maxLat - minLat;
      const areaKm2 = width * height * 111 * 111; // Rough conversion to km²
      const areaAcres = areaKm2 * 247.105; // Convert km² to acres

      return areaAcres;
    } catch (error) {
      console.error('Error calculating area:', error);
      return 0;
    }
  };

  // Use shared utility function for area calculation
  const calculatePolygonArea = calculatePolygonAreaFromFeature;

  // Load saved map bounds from localStorage
  const getSavedBounds = () => {
    const saved = localStorage.getItem('fieldMapBounds');
    return saved ? JSON.parse(saved) : null;
  };

  // Save map bounds to localStorage
  const saveBounds = (bounds: any) => {
    try {
      // Validate bounds before saving
      if (bounds && bounds._sw && bounds._ne &&
          !isNaN(bounds._sw.lng) && !isNaN(bounds._sw.lat) &&
          !isNaN(bounds._ne.lng) && !isNaN(bounds._ne.lat)) {


        localStorage.setItem('fieldMapBounds', JSON.stringify(bounds));
      } else {

      }
    } catch (error) {
      console.error('❌ Error saving bounds:', error);
    }
  };

  // Get saved loaded county from localStorage
  const getSavedLoadedCounty = () => {
    try {
      const saved = localStorage.getItem('fieldMapLoadedCounty');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
      return null;
    } catch (error) {
      console.error('❌ Error parsing saved loaded county:', error);
      return null;
    }
  };

  // Save loaded county to localStorage
  const saveLoadedCounty = (countyKey: string) => {
    try {
      if (countyKey) {
        localStorage.setItem('fieldMapLoadedCounty', JSON.stringify({ countyKey }));
      }
    } catch (error) {
      console.error('❌ Error saving loaded county:', error);
    }
  };

  // Clear saved loaded county from localStorage
  const clearSavedLoadedCounty = () => {
    try {
      localStorage.removeItem('fieldMapLoadedCounty');
    } catch (error) {
      console.error('❌ Error clearing saved loaded county:', error);
    }
  };

  const handlePolygonClick = (polygon: any) => {
    // Don't handle polygon clicks when in field editing mode to prevent UI state conflicts
    if (isFieldEditingMode) {
      return;
    }
    
    setSelectedPolygon(polygon);
    setShowForm(true);
    setIsSelectMode(false); // Turn off select mode when polygon is selected
  };

  const handleMapReady = (isReady: boolean) => {
    // Map is ready, but we get the map reference from onMapLoad
  };

  const handleMapLoad = (map: any) => {
    // Only initialize once to prevent restoring saved bounds on re-renders
    //if (mapInitialized) {
    //  mapRef.current = map;
    //  return;
    //}

    mapRef.current = map;
    setMapInitialized(true);

    // Restore saved bounds if available (only on first load)
    const savedBounds = getSavedBounds();
    if (savedBounds) {
      try {
        // Validate saved bounds before using them
        if (savedBounds._sw && savedBounds._ne &&
            !isNaN(savedBounds._sw.lng) && !isNaN(savedBounds._sw.lat) &&
            !isNaN(savedBounds._ne.lng) && !isNaN(savedBounds._ne.lat)) {
            const bounds = [[savedBounds._sw.lng, savedBounds._sw.lat],
                [savedBounds._ne.lng, savedBounds._ne.lat]];
            map.fitBounds(bounds, { padding: 50, animate: true, duration: 1000 });
        } else {
          console.warn('⚠️ Invalid saved bounds format, skipping restore:', savedBounds);
        }
      } catch (error) {
        console.error('❌ Error restoring saved bounds:', error);
      }
    }

    // Restore previously loaded county tiles if available
    // Use a small delay to ensure the map is fully ready
    setTimeout(() => {
      const savedLoadedCounty = getSavedLoadedCounty();
      
      if (savedLoadedCounty && savedLoadedCounty.countyKey && mapRef.current) {
        try {
          
          // Set the current county
          setCurrentCounty(savedLoadedCounty.countyKey);
          // Load the county tiles using mapRef.current
          if (mapRef.current.loadCountyTiles) {
            mapRef.current.loadCountyTiles(savedLoadedCounty.countyKey);
            // Set polygonsLoaded to true since we're restoring existing tiles
            setPolygonsLoaded(true);
          } else {
            console.warn('⚠️ loadCountyTiles method not available on map yet');
          }
        } catch (error) {
          console.error('❌ Error restoring saved county tiles:', error);
        }
      } else {
      }
    }, 500); // 500ms delay to ensure map is fully ready
  };

  const handleMapMove = () => {
    if (mapRef.current) {
        //const bounds = mapRef.current.getBounds();
        //  saveBounds(bounds);
    }
  };

  const handleTileLoadingChange = (isLoading: boolean) => {
    if (!isLoading) {
      // Tiles have finished loading, enable the Select button
      setPolygonsLoaded(true);
    } else {
      // Tiles are loading, disable the Select button
      setPolygonsLoaded(false);
    }
  };

  const handleCountySelect = (countyKey: string) => {

    // Check if county has actually changed
    const countyChanged = previousCountyRef.current !== countyKey;
    
    setCurrentCounty(countyKey);
    setPolygonsLoaded(false); // Reset when new county is selected
   // console.log('🔄 handleCountySelect setting polygonsLoaded to false');
    previousCountyRef.current = countyKey; // Update the previous county ref

    // Clear saved loaded county when selecting a new county
    if (countyChanged) {
      clearSavedLoadedCounty();
    }

    // Only auto-zoom if the county has actually changed
    if (countyChanged) {
      // Auto-zoom to the selected county
      // We need to fetch the counties data to get the centroid
      fetch('/counties.json')
        .then(response => response.json())
        .then(counties => {
          const county = counties.find((c: any) => c.county_key === countyKey);
          if (county && county.centroid) {
            const [lng, lat] = county.centroid;
            if (typeof lng === 'number' && typeof lat === 'number') {
              handleZoomToLocation(lat, lng, 10);
              // Store the full county data for later use
              setCurrentCountyData(county);
            } else {
              console.error('❌ Invalid centroid coordinates:', county.centroid);
            }
          } else {
            console.error('❌ County or centroid not found:', county);
          }
        })
        .catch(error => {
          console.error('❌ Error loading counties data:', error);
        });
    } else {
      // County hasn't changed, just load the county data without zooming
      fetch('/counties.json')
        .then(response => response.json())
        .then(counties => {
          const county = counties.find((c: any) => c.county_key === countyKey);
          if (county) {
            setCurrentCountyData(county);
          }
        })
        .catch(error => {
          console.error('❌ Error loading counties data:', error);
        });
    }
  };

  const handleFetchPolygons = (countyKey: string) => {
    if (polygonsLoaded) {
      // Hide polygons - clear the county data
      if (mapRef.current && mapRef.current.clearCountyData) {
        mapRef.current.clearCountyData();
      }
      setCurrentCounty(null);
      setPolygonsLoaded(false);
      clearSavedLoadedCounty();
    } else {
      // Load polygons
      setCurrentCounty(countyKey);
      if (mapRef.current && mapRef.current.loadCountyTiles) {
        mapRef.current.loadCountyTiles(countyKey);
        // Save the loaded county to localStorage
        saveLoadedCounty(countyKey);
        // Don't set polygonsLoaded here - it will be set when tiles are actually loaded
      }
    }
  };

  const handleDrawingModeChange = (isDrawing: boolean) => {
    setIsDrawingMode(isDrawing);
  };

  const handleSelectModeChange = (isSelect: boolean) => {
    setIsSelectMode(isSelect);
  };

  const handleDrawFieldBoundary = () => {
    if (isFieldDrawingMode) {
      // If we're exiting field drawing mode, check if we have points to complete
      if (mapRef.current && mapRef.current.getFieldDrawingPoints) {
        const points = mapRef.current.getFieldDrawingPoints();
        if (points.length >= 3) {
          // Complete the field drawing
          mapRef.current.completeFieldDrawing();
        } else {
          // Just exit drawing mode without completing
          setIsFieldDrawingMode(false);
        }
      } else {
        setIsFieldDrawingMode(false);
      }
    } else {
      // Enter field drawing mode - clear any existing polygon first
      if (mapRef.current && mapRef.current.clearFieldDrawing) {
        mapRef.current.clearFieldDrawing();
      }
      setIsFieldDrawingMode(true);
      setIsDrawingMode(false);
      setIsSelectMode(false);
    }
  };

  const handleFieldDrawingComplete = (polygon: any) => {
    // Set the selected polygon and show the form
    setSelectedPolygon(polygon);
    setShowForm(true);
    setIsFieldDrawingMode(false);
    setFieldDrawingPointsCount(0);
    setIsFieldEditingMode(false);
  };

  // Handle delete field drawing
  const handleDeleteFieldDrawing = () => {
    if (mapRef.current && mapRef.current.clearFieldDrawing) {
      mapRef.current.clearFieldDrawing();
      setFieldDrawingPointsCount(0);
      setIsFieldEditingMode(false);
      setIsFieldDrawingMode(false); // Reset field drawing mode to show "Draw Field Boundary" button
    }
  };

  // Handle enter edit mode for field drawing
  const handleEditFieldDrawing = () => {
    try {
      // Safety check: ensure we have enough points and map is ready
      if (!mapRef.current) {
        console.error('❌ Map not ready for edit mode');
        return;
      }
      
      if (fieldDrawingPointsCount < 3) {
        console.error('❌ Need at least 3 points to enter edit mode');
        return;
      }
      
      console.log('🔧 Entering field drawing edit mode...');
      setIsFieldEditingMode(true);
    } catch (error) {
      console.error('❌ Error entering edit mode:', error);
      setError('Failed to enter edit mode. Please try again.');
    }
  };

  // Handle exit edit mode for field drawing
  const handleExitFieldDrawingEdit = () => {
    setIsFieldEditingMode(false);
  };

  // Effect to monitor field drawing points count
  useEffect(() => {
    if (!isFieldDrawingMode || !mapRef.current) {
      setFieldDrawingPointsCount(0);
      return;
    }

    const checkPointsInterval = setInterval(() => {
      if (mapRef.current && mapRef.current.getFieldDrawingPoints) {
        const points = mapRef.current.getFieldDrawingPoints();
        setFieldDrawingPointsCount(points.length);
      }
    }, 100);

    return () => clearInterval(checkPointsInterval);
  }, [isFieldDrawingMode]);

  const handleDrawingBounds = (bounds: any) => {
    if (!mapRef.current) {
      console.warn('⚠️ Map not ready');
      return;
    }

    // Extract the bounds coordinates
    const sw = bounds._sw; // Southwest corner
    const ne = bounds._ne; // Northeast corner

    if (!sw || !ne) {
      console.error('❌ Invalid bounds format:', bounds);
      return;
    }

    // Zoom to the drawn bounds to show the selected area
    try {
      mapRef.current.fitBounds(bounds, {
        padding: 50,
        duration: 1000,
        essential: true
      });
    } catch (error) {
      console.error('❌ Error zooming to drawn bounds:', error);
    }
  };

  const handleZoomToLocation = (lat: number, lng: number, zoom: number) => {
    // Validate coordinates
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
      console.error('❌ Invalid coordinates:', { lat, lng });
      return;
    }

    if (mapRef.current) {
      try {
        mapRef.current.flyTo({
          center: [lng, lat],
          zoom: zoom,
          duration: 2000
        });
      } catch (error) {
        console.error('❌ Error flying to location:', error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent, stayOpen: boolean = false) => {
    e.preventDefault();
    if (!selectedPolygon || !fieldName.trim()) return;

    // Check for duplicate field name within the same farm (only for new fields, not when updating)
    if (!targetField) {
      const trimmedFieldName = fieldName.trim();
      const duplicateField = existingFields?.find(field => 
        field.farm_id === farmId && 
        field.fieldname.toLowerCase() === trimmedFieldName.toLowerCase()
      );

      if (duplicateField) {
        setError(`A field named "${trimmedFieldName}" already exists in this farm`);
        return;
      }
    }

    if (stayOpen) {
      setSavingForStayOpen(true);
    } else {
      setIsSaving(true);
    }
    setError('');

    try {


      // Derive climate zone from state/county or use default
      const getClimateZone = async () => {
        if (currentCountyData?.climateZone) {
          return currentCountyData.climateZone;
        }
        
        // Try to look up climate zone from counties data
        try {
          const response = await fetch('/counties.json');
          const counties = await response.json();
          const state = currentCountyData?.state_name || fieldState;
          const county = currentCountyData?.county || fieldCounty;
          
          // Find matching county
          const matchingCounty = counties.find((c: any) => 
            c.state_name?.toLowerCase() === state?.toLowerCase() && 
            c.county?.toLowerCase() === county?.toLowerCase()
          );
          
          if (matchingCounty?.climateZone) {
            return matchingCounty.climateZone;
          }
        } catch (error) {
          console.warn('Could not load counties data for climate zone lookup:', error);
        }
        
        // Fallback to default based on state
        const state = currentCountyData?.state_name || fieldState;
        if (state?.toLowerCase().includes('iowa')) {
          return '5a'; // Default for Iowa
        }
        return '5a'; // Default fallback
      };

      const climateZone = await getClimateZone();
      
      // Prepare field data - different structure for create vs update
      const baseFieldData = {
        fieldname: fieldName.trim(),
        acres: calculatePolygonAreaFromFeature(selectedPolygon),
        geojson: JSON.stringify(selectedPolygon.geometry),
        state: currentCountyData?.state_name || fieldState,
        county: currentCountyData?.county || fieldCounty,
        cdl2024: selectedPolygon.properties?.CDL2024 || '',
        climate_zone: climateZone
      };

      // For creating new fields, include farm_id. For updating, exclude it.
      const fieldData = targetField 
        ? baseFieldData 
        : { ...baseFieldData, farm_id: farmId };

      let response;
      let savedField;

      if (targetField) {
        // Update existing field with boundary
        const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
        const finalUrl = `${API_ENDPOINTS.FIELDS}/${targetField.field_id}${organizationParam}`;

        response = await fetch(finalUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`,
          },
          body: JSON.stringify(fieldData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update field boundary');
        }

        savedField = await response.json();
      } else {
        // Create new field
        const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
        const finalUrl = API_ENDPOINTS.FIELDS + organizationParam;

        response = await fetch(finalUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`,
          },
          body: JSON.stringify(fieldData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create field');
        }

        savedField = await response.json();
      }

      onSave(savedField);
      
      if (stayOpen) {
        // Stay open to add another field
        handleSaveAndStayOpen();
      } else {
        // Close the modal
        handleModalClose();
      }
    } catch (err) {
      console.error('Error creating field:', err);
      setError(err instanceof Error ? err.message : 'Failed to create field');
    } finally {
      if (stayOpen) {
        setSavingForStayOpen(false);
      } else {
        setIsSaving(false);
      }
    }
  };

  // New function to handle saving and staying open
  const handleSaveAndStayOpen = () => {
    // Reset form for next field
    setFieldName('');
    setFieldState('');
    setFieldCounty('');
    setError('');
    
    // Clear the selected polygon and return to map view
    setSelectedPolygon(null);
    setShowForm(false);
    
    // Remove the selection marker if it exists
    if (mapRef.current) {
      if (mapRef.current.getLayer('selection-marker')) {
        mapRef.current.removeLayer('selection-marker');
      }
      if (mapRef.current.getSource('selection-marker')) {
        mapRef.current.removeSource('selection-marker');
      }
    }
    
    // Refresh the existing fields layer to show the newly added field
    if (mapRef.current && mapRef.current.refreshExistingFields) {
      mapRef.current.refreshExistingFields();
    }
    
    // Show success message briefly
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
    
    console.log('✅ Field saved successfully! Ready to add another field.');
  };

  const handleCancel = () => {
    setSelectedPolygon(null);
    setFieldName('');
    setFieldState('');
    setFieldCounty('');
    setShowForm(false);
    setError('');

    // Remove the selection marker if it exists
    if (mapRef.current) {
      if (mapRef.current.getLayer('selection-marker')) {
        mapRef.current.removeLayer('selection-marker');
      }
      if (mapRef.current.getSource('selection-marker')) {
        mapRef.current.removeSource('selection-marker');
      }
    }
  };

  const handleModalClose = () => {
    // Save current map bounds before closing
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      saveBounds(bounds);

    }

    // Don't clear saved loaded county - let it persist for next session
    // clearSavedLoadedCounty(); // REMOVED - we want to remember the loaded county

    // Reset all state when modal is closed
    setSelectedPolygon(null);
    setFieldName('');
    setFieldState('');
    setFieldCounty('');
    setShowForm(false);
    setError('');
    setCurrentCounty(null);
    setCurrentCountyData(null);
    setPolygonsLoaded(false);
    setIsDrawingMode(false);
    setIsSelectMode(false);
    setIsFieldDrawingMode(false);
    setIsFieldEditingMode(false);
    setFieldDrawingPointsCount(0);

    // Remove the selection marker if it exists
    if (mapRef.current) {
      if (mapRef.current.getLayer('selection-marker')) {
        mapRef.current.removeLayer('selection-marker');
      }
      if (mapRef.current.getSource('selection-marker')) {
        mapRef.current.removeSource('selection-marker');
      }
      // Clear any field drawing if it exists
      if (mapRef.current.clearFieldDrawing) {
        mapRef.current.clearFieldDrawing();
      }
    }

    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay field-map-modal">
      <div className="modal-content field-map-content">
        <div className="modal-header">
          <h2>{targetField ? `Add Boundary for ${targetField.fieldname}` : `Select Field for ${farmName}`}</h2>
          <button className="modal-close" onClick={handleModalClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="field-map-modal-field-map-container">
          {!showForm ? (
            <div className="map-instructions">
              {showSuccessMessage && (
                <div className="success-message">
                  <p>✅ Field saved successfully! Ready to add another field.</p>
                </div>
              )}
              {shouldShowManualDrawMode() ? (
                <>
                  {targetField ? (
                    <>
                      <p><strong>Adding boundary for:</strong> {targetField.fieldname}</p>
                      <p>Choose the State and County to set your location context.</p>
                      <p>Use Zooming and Panning to navigate to your field location.</p>
                      <p>Click "Draw Field Boundary" to start drawing the boundary for this field.</p>
                      <p>Click points on the map to create the field boundary. A live polygon will appear after 3 points.</p>
                      <p>Click near the first point or click "Exit Field Drawing" to complete the boundary.</p>
                    </>
                  ) : (
                    <>
                      <p>Choose the State and County to set your location context.</p>
                      <p>Use Zooming and Panning and/or Zoom to Rectangle to navigate to your farm area.</p>
                      <p>Click "Draw Field Boundary" to start drawing your field polygon.</p>
                      <p>Click points on the map to create your field boundary. A live polygon will appear after 3 points.</p>
                      <p>Click near the first point or click "Exit Field Drawing" to complete the field.</p>
                      <p>Previously set up fields are shown in <span className="selected-color">blue</span>.</p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p>Choose the State and County.</p>
                  <p>Locate your farm area using Zooming and Panning and/or Zoom to Rectangle.</p>
                  <p>Click &quot;Draw Field Boundary&quot; to start drawing your field polygon.</p>
                  <p>Click points on the map to create your field boundary. A live polygon will appear after 3 points.</p>
                  <p>Click near the first point or click &quot;Exit Field Drawing&quot; to complete the field.</p>
                  <p>Previously set up fields are shown in <span className="selected-color">blue</span>.</p>
                </>
              )}

              {/* Boundary mode selection (Draw vs Fetch) - disabled: tile server boundaries not accurate enough
              <div className="boundary-mode-selection">
                <label>Boundary Selection Mode:</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="boundaryMode"
                      value="select"
                      checked={boundaryMode === 'select'}
                      onChange={(e) => setBoundaryMode(e.target.value as 'draw' | 'select')}
                    />
                    Select Boundary from the US Cropped Fields Polygons
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="boundaryMode"
                      value="draw"
                      checked={boundaryMode === 'draw'}
                      onChange={(e) => setBoundaryMode(e.target.value as 'draw' | 'select')}
                    />
                    Manually Draw Boundary
                  </label>
                </div>
              </div>
              */}

            </div>
          ) : (
            <div className="field-form">
              <h3>Field Details</h3>
              <p>Selected area: {selectedPolygon ? calculateArea(selectedPolygon).toFixed(2) : 'Unknown'} acres</p>

              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="form-group">
                  <label htmlFor="fieldName">Field Name *</label>
                  <input
                    type="text"
                    id="fieldName"
                    value={fieldName}
                    onChange={(e) => setFieldName(toProperCase(e.target.value))}
                    required
                    placeholder="Enter field name"
                    autoFocus
                    autoComplete="off"
                    disabled={!!targetField}
                  />
                  {targetField && (
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Adding boundary to existing field
                    </small>
                  )}
                </div>

                {/* Show location fields if county data is not available */}
                {!currentCountyData && (
                  <>
                    <div className="form-group">
                      <label htmlFor="fieldState">State *</label>
                      <input
                        type="text"
                        id="fieldState"
                        value={fieldState}
                        onChange={(e) => setFieldState(e.target.value)}
                        required
                        placeholder="Enter state (e.g., Iowa)"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="fieldCounty">County *</label>
                      <input
                        type="text"
                        id="fieldCounty"
                        value={fieldCounty}
                        onChange={(e) => setFieldCounty(e.target.value)}
                        required
                        placeholder="Enter county name"
                      />
                    </div>
                  </>
                )}

                {error && (
                  <div className="error-message">
                    <p>{error}</p>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  {!targetField && (
                    <button
                      type="button"
                      onClick={(e) => handleSubmit(e, true)}
                      className="btn-primary"
                      disabled={savingForStayOpen || isSaving || !fieldName.trim() || (!currentCountyData && (!fieldState.trim() || !fieldCounty.trim()))}
                    >
                      {savingForStayOpen ? 'Creating...' : 'Save & Add Another'}
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingForStayOpen || isSaving || !fieldName.trim() || (!currentCountyData && (!fieldState.trim() || !fieldCounty.trim()))}
                  >
                    {isSaving ? (targetField ? 'Updating...' : 'Creating...') : (targetField ? 'Save Boundary' : 'Save & Close')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="field-map-modal-map-container">
            <MapToolbar
              onCountySelect={handleCountySelect}
              onZoomToLocation={handleZoomToLocation}
              onFetchPolygons={handleFetchPolygons}
              onDrawingModeChange={handleDrawingModeChange}
              onSelectModeChange={handleSelectModeChange}
              onDrawFieldBoundary={handleDrawFieldBoundary}
              isDrawingMode={isDrawingMode}
              isFieldDrawingMode={isFieldDrawingMode}
              isSelectMode={isSelectMode}
              currentCounty={currentCounty || undefined}
              polygonsLoaded={polygonsLoaded}
              isManualDrawMode={shouldShowManualDrawMode()}
              externalState={farmState}
              externalCounty={farmCounty}
              showDeleteEditButtons={fieldDrawingPointsCount >= 3}
              onDeleteFieldDrawing={handleDeleteFieldDrawing}
              onEditFieldDrawing={handleEditFieldDrawing}
              onExitFieldDrawingEdit={handleExitFieldDrawingEdit}
              isFieldEditingMode={isFieldEditingMode}
            />

            {/* Info Panel Button - Mobile Only */}
            {boundaryMode === 'select' && (
              <button
                onClick={() => setIsInfoPanelModalOpen(true)}
                className="info-panel-toggle-btn"
                title="Important Information"
              >
                ⚠️ Important Info
              </button>
            )}

            {/* Legend Button - Top Right */}
            {polygonsLoaded && (
              <button
                onClick={() => setIsLegendOpen(!isLegendOpen)}
                className="legend-toggle-btn"
                title={isLegendOpen ? 'Hide Legend' : 'Show Legend'}
              >
                {isLegendOpen ? 'Hide Legend' : 'Show Legend'}
              </button>
            )}

            {/* Crop Legend */}
            <CropLegend
              isOpen={isLegendOpen}
              onToggle={() => setIsLegendOpen(!isLegendOpen)}
              visibleCrops={getVisibleCrops()}
            />

            <MapboxMap
              onMapLoad={handleMapLoad}
              onMapReady={handleMapReady}
              onMapMove={handleMapMove}
              onPolygonClick={handlePolygonClick}
              existingFields={existingFields}
              isFieldSelectionMode={true}
              isDrawingMode={isDrawingMode}
              isFieldDrawingMode={isFieldDrawingMode}
              isFieldEditingMode={isFieldEditingMode}
              onDrawingModeChange={handleDrawingModeChange}
              onDrawingBounds={handleDrawingBounds}
              onFieldDrawingComplete={handleFieldDrawingComplete}
              isSelectMode={isSelectMode}
              onTileLoadingChange={handleTileLoadingChange}
              onVisibleCropsChange={handleVisibleCropsChange}
            />
          </div>
        </div>

        {/* Important Information Panel - only show when boundary selection mode is active */}
        {boundaryMode === 'select' && (
          <>
            <div className={`info-panel info-panel-desktop ${isInfoPanelCollapsed ? 'collapsed' : ''}`}>
              <button 
                className="info-panel-toggle"
                onClick={() => setIsInfoPanelCollapsed(!isInfoPanelCollapsed)}
                title={isInfoPanelCollapsed ? 'Show Important Information' : 'Hide Important Information'}
              >
                ⚠️ Important Information {isInfoPanelCollapsed ? '▼' : '▲'}
              </button>
              {!isInfoPanelCollapsed && (
                <div className="info-panel-content">
                  <p>
                    <strong>Please Note:</strong> The Cropped Area polygons are created using AI and may not always be accurate. 
                    If you are happy with the field boundaries that's good. If not, you have 2 choices:
                  </p>
                  <ol>
                    <li><strong>Select the appropriate polygon</strong> for your field and edit it later back on the setup tab (recommended if the polygons are closely matched to the actual field boundaries), or</li>
                    <li><strong>Choose to Manually Draw</strong> the field boundary (recommended if the polygons are not closely matched to the actual field boundaries) </li>
                  </ol>
                </div>
              )}
            </div>

            {/* Info Panel Modal - Mobile Only */}
            {isInfoPanelModalOpen && (
              <div className="info-panel-modal-overlay" onClick={() => setIsInfoPanelModalOpen(false)}>
                <div className="info-panel-modal-content" onClick={(e) => e.stopPropagation()}>
                  <button 
                    className="info-panel-modal-close" 
                    onClick={() => setIsInfoPanelModalOpen(false)}
                  >
                    ✕
                  </button>
                  <div className="info-panel-content">
                    <p>
                      <strong>Please Note:</strong> The Cropped Area polygons are created using AI and may not always be accurate. 
                      If you are happy with the field boundaries that's good. If not, you have 2 choices:
                    </p>
                    <ol>
                      <li><strong>Select the appropriate polygon</strong> for your field and edit it later back on the setup tab (recommended if the polygons are closely matched to the actual field boundaries), or</li>
                      <li><strong>Choose to Manually Draw</strong> the field boundary (Recommended if the polygons are not closely matched to the actual field boundaries) </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default FieldMapModal;
